import { env } from '@/config/env';
import type { PlatformIdentity } from '@/platform/types';

/**
 * Сессия по подписи площадки (ADR-041).
 *
 * Направление связи перевёрнуто: не «сессия получает метку площадки», а
 * «подпись площадки определяет, чей это user_id». Клиент лишь пересылает
 * сырые initData/launch params — подлинность проверяет сервер, поэтому
 * функция и отвечает без пользовательского JWT.
 *
 * Ответ бывает двух видов: сессия уже принадлежит владельцу площадки
 * (`current`, входить некуда) либо сервер выдал одноразовый токен для
 * входа (`minted`).
 */
export type PlatformSessionResult = { status: 'current' } | { status: 'minted'; tokenHash: string };

/** Ошибка, пригодная для показа: текст пишет сервер, код — для ветвления. */
export class PlatformSessionError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'PlatformSessionError';
    this.code = code;
  }
}

export interface PlatformSessionSignals {
  /** Слабый сигнал устройства для ограничения стартовых начислений. */
  deviceHash: string;
  /**
   * Код приглашения уходит именно сюда: профиль платформенного
   * пользователя создаётся в этой функции, а onboard_user заводит
   * реферала только вместе с профилем — позже будет уже поздно.
   */
  referralCode?: string | undefined;
}

interface ServerPayload {
  status?: string;
  tokenHash?: string;
  error?: { code?: string; message?: string };
}

export async function requestPlatformSession(
  identity: Exclude<PlatformIdentity, null>,
  signals: PlatformSessionSignals,
  accessToken?: string | undefined,
): Promise<PlatformSessionResult> {
  let response: Response;

  try {
    response = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/platform-session`, {
      method: 'POST',
      headers: {
        // Сессии может не быть вовсе — это нормальный первый заход в
        // Mini App. Пустой Authorization сервер счёл бы за испорченный
        // токен, поэтому заголовок добавляется только когда есть что слать.
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        apikey: env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...identity,
        deviceHash: signals.deviceHash,
        ...(signals.referralCode ? { referralCode: signals.referralCode } : {}),
      }),
    });
  } catch {
    throw new PlatformSessionError(
      'Нет связи с сервером. Проверьте интернет и откройте приложение снова.',
      'NETWORK_ERROR',
    );
  }

  const payload = (await response.json().catch(() => null)) as ServerPayload | null;

  if (!payload) {
    throw new PlatformSessionError(
      'Сервер ответил неожиданно. Откройте приложение снова.',
      'UNKNOWN_ERROR',
    );
  }

  if (!response.ok || payload.error) {
    throw new PlatformSessionError(
      payload.error?.message ?? 'Не удалось войти. Откройте приложение снова.',
      payload.error?.code ?? 'UNKNOWN_ERROR',
    );
  }

  if (payload.status === 'current') {
    return { status: 'current' };
  }

  /*
   * «minted» без токена входа — это не успех: молча продолжить с чужой
   * (или отсутствующей) сессией опаснее, чем честно показать ошибку.
   */
  if (payload.status === 'minted' && payload.tokenHash) {
    return { status: 'minted', tokenHash: payload.tokenHash };
  }

  throw new PlatformSessionError(
    'Сервер ответил неожиданно. Откройте приложение снова.',
    'UNKNOWN_ERROR',
  );
}
